import { useQuery } from "@tanstack/react-query";

import { api } from "@utils/api";

import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { ClinicFile } from "@customTypes/clinic";
import { Pagination } from "@customTypes/index";

import { clinicQKeys } from "./queryKeys";


interface GetClinicFilesParams {
  search?: string;
  doctorId?: string;
}

interface GetClinicFilesResponse {
  newData: ClinicFile[];
  pagination: Pagination;
}

const queryFn = ({ search, doctorId }: GetClinicFilesParams) => {
  return api.get("clinic/files", {
    params: {
      ...(search && { search }),
      ...(doctorId && { doctor_id: doctorId }),
    },
  });
};

// Helper function to extract filename from URL
const extractFilenameFromUrl = (url: string): string => {
  if (!url) return "";
  const parts = url.split("/");
  return parts[parts.length - 1] || "";
};

const select = (res: any): GetClinicFilesResponse => {
  // Check if response has pagination structure
  if (res.data.data) {
    const pagination: Pagination = {
      page: res.data.pagination.page,
      pageCount: res.data.pagination.page_count,
      pageSize: res.data.pagination.page_size,
      total: res.data.pagination.total,
    };

    const newData: ClinicFile[] = [];

    for (const i in res.data.data) {
      const item = res.data.data[i];
      newData.push({
        id: item._id || item.id,
        fileName: extractFilenameFromUrl(item.file || item.url || ""),
        tag: item.type || item.tag || "",
        dateUploaded:
          item.dateUploaded || item.date_uploaded || item.created_at,
        assignedDoctor: item.related_user || "",
        size: item.size,
        type: item.type,
        url: item.file || item.url,
        createdAt: item.created_at
          ? new Date(item.created_at)
          : new Date(1900, 0, 1),
        updatedAt: item.created_at
          ? new Date(item.updated_at)
          : new Date(1900, 0, 1),
      });
    }

    return {
      newData,
      pagination,
    };
  }

  // Fallback for non-paginated response (direct array)
  if (Array.isArray(res.data)) {
    const newData: ClinicFile[] = res.data.map((item: any) => ({
      id: item._id || item.id,
      fileName: extractFilenameFromUrl(item.file || item.url || ""),
      tag: item.type || item.tag || "",
      dateUploaded: item.date_uploaded
        ? new Date(item.date_uploaded)
        : new Date(1900, 0, 1),
      assignedDoctor: item.related_user || "",
      size: item.size,
      type: item.type,
      url: item.file || item.url,
      createdAt: item.created_at
        ? new Date(item.created_at)
        : new Date(1900, 0, 1),
      updatedAt: item.updated_at
        ? new Date(item.updated_at)
        : new Date(1900, 0, 1),
    }));

    return {
      newData,
      pagination: {
        page: 1,
        pageCount: 1,
        pageSize: 25, // Use consistent default pageSize instead of newData.length
        total: newData.length,
      },
    };
  }

  return {
    newData: [],
    pagination: {
      page: 1,
      pageCount: 1,
      pageSize: 25, // Use consistent default pageSize instead of 0
      total: 0,
    },
  };
};

export const useGetClinicFiles = (params: GetClinicFilesParams = {}) => {
  const handleErrors = useQueryErrorHandling();

  return useQuery({
    queryKey: clinicQKeys.filesWithFilters({
      search: params.search,
      doctorId: params.doctorId,
    }),
    queryFn: () => queryFn(params),
    select,
    staleTime: 5 * 60 * 1000, // 5 minutes

    onError: (res: any) => {
      console.error("[From Get Clinic Files]", res);
      handleErrors(res, "query");
    },
  });
};
